const { Core, Events } = Global;

const BORDER_COMPENSATION = 4;
const OFFSET_STYLE_PROP = '--offset-scroll-y';
const ENHANCED_CONTROLS_OFFSET_STYLE_PROP = '--product-gallery-enhanced-controls-offset';
const ENHANCED_MAIN_ARROW_TOP_STYLE_PROP = '--product-gallery-main-arrow-top';
const POSITION_CLASS_PREFIX = '@position:';
const ENHANCED_POSITION_CLASS = '@position:bottom';
const SCROLL_EPSILON = 1;
const SCROLL_SETTLE_DELAY = 120;
const ENHANCED_SCROLL_DURATION = 350;
const MOBILE_MEDIA_QUERY = window.matchMedia('(max-width: 991px)');

function isElementValueEquals(value) {
    return (el) => el.value === value.toString();
}

customElements.define('product-gallery-thumbnails', class extends Core {

    propTypes = {
        'auto-scroll': Boolean
    }

    elements = {
        $: [['input']]
    }

    connectedCallback() {
        if(!this.movingEnhancedWrapper) {
            super.connectedCallback();
        }
    }

    disconnectedCallback() {
        if(!this.movingEnhancedWrapper) {
            super.disconnectedCallback();
        }
    }

    render() {
        this.sub(Events.PRODUCT_GALLERY_SLIDE_CHANGE, this._changeThumb);
        this.$({ change: this._handleChange });
        if(this.enhancedMode === undefined) {
            this.enhancedMode = false;
        }
    }

    _handleChange({ target }) {
        this.pub(Events.PRODUCT_GALLERY_SLIDE_CHANGE, +target.value);
        this._scrollToTarget(target);
    }

    refreshInputs() {
        this._elements.delete('input');
        this.height = null;
        this.parentHeight = null;
        this.halfParentHeight = null;
        this.style.removeProperty(OFFSET_STYLE_PROP);
        this._scheduleEnhancedRefresh();
    }

    _scrollToTarget(target) {
        if(this.enhancedMode) {
            this._ensureEnhancedThumbnailVisible(target.labels[0]);
            return;
        }
        if(!this.prop('auto-scroll')) {
            return;
        }
        const label = target.labels[0];
        if(label) {
            this.style.setProperty(OFFSET_STYLE_PROP, `${this._calcOffset(label)}px`);
        }
    }

    _calcOffset(el) {

        // calculating all values once
        if(!this.height || !this.parentHeight || !this.halfParentHeight) {
            this.height = this.offsetHeight;
            this.parentHeight = this.parentNode.offsetHeight;

            // check if parent height is not calculated properly (styles are not loaded)
            if(this.parentHeight > window.innerHeight) {
                this.parentHeight = null;
                return 0;
            }

            if(this.parentHeight >= this.height) {
                return 0;
            }

            this.halfParentHeight = this.parentHeight / 2;
        }

        return Math.max(
            Math.min((el.offsetTop * -1) + this.halfParentHeight, 0),
            this.parentHeight - this.height - BORDER_COMPENSATION
        );
    }

    _changeThumb(index) {
        const currentThumb = this.$('input').find(isElementValueEquals(index));
        if(currentThumb) {
            currentThumb.checked = true;
            this._scrollToTarget(currentThumb);
        }
    }

    setEnhancedMode(enabled) {
        enabled = Boolean(enabled);

        if(this.enhancedMode === undefined && !enabled) {
            this.enhancedMode = false;
            this.removeAttribute('data-gallery-enhanced');
            return enabled;
        }

        if(enabled === this.enhancedMode) {
            this._scheduleEnhancedRefresh();
            return enabled;
        }

        this.enhancedMode = enabled;
        enabled ? this._enableEnhancedMode() : this._disableEnhancedMode();
        this.toggleAttribute('data-gallery-enhanced', enabled);
        this._scheduleEnhancedRefresh();
        return enabled;
    }

    _enableEnhancedMode() {
        const wrapper = this.closest('[data-product-gallery-thumbnails-wrapper]');
        const gallery = this.closest('[data-product-gallery]');
        const pagination = gallery?.querySelector('product-gallery-slider .blaze-pagination');

        this.enhancedControlState = {
            wrapper,
            gallery,
            pagination,
            wrapperParent: wrapper?.parentNode,
            wrapperNextSibling: wrapper?.nextSibling || null,
            positionClass: Array.from(this.classList).find((className) => {
                return className.startsWith(POSITION_CLASS_PREFIX);
            }),
            galleryPositionClass: Array.from(gallery?.classList || []).find((className) => {
                return className.startsWith('@thumbs-position:');
            }),
            autoScroll: this.hasAttribute('auto-scroll'),
            scrollLeft: this.scrollLeft
        };

        if(this.enhancedControlState.positionClass) {
            this.classList.remove(this.enhancedControlState.positionClass);
        }
        this.classList.add(ENHANCED_POSITION_CLASS);
        this.removeAttribute('auto-scroll');
        this.style.removeProperty(OFFSET_STYLE_PROP);

        if(gallery) {
            if(this.enhancedControlState.galleryPositionClass) {
                gallery.classList.remove(this.enhancedControlState.galleryPositionClass);
            }
            gallery.classList.add('@thumbs-position:bottom');
        }

        this.previousButton = wrapper?.querySelector('[data-element="previous"]');
        this.nextButton = wrapper?.querySelector('[data-element="next"]');
        this.pagePagination = wrapper?.querySelector('[data-element="thumbnail-pagination"]');
        this.pageProgress = wrapper?.querySelector('[data-element="thumbnail-progress"]');
        this._handleEnhancedScroll = () => {
            this._syncEnhancedPageFromScroll();
            clearTimeout(this.enhancedScrollTimer);
            this.enhancedScrollTimer = setTimeout(() => {
                this._snapToNearestEnhancedPage();
            }, SCROLL_SETTLE_DELAY);
        };
        this._handleEnhancedInteraction = () => {
            cancelAnimationFrame(this.enhancedScrollAnimationFrame);
            this.enhancedScrollAnimationFrame = null;
            if(this.enhancedScrollSnapType !== null) {
                this.style.scrollSnapType = this.enhancedScrollSnapType;
                this.enhancedScrollSnapType = null;
            }
            this.enhancedTargetPage = null;
        };
        this._handlePrevious = () => this._navigateEnhancedRail(-1);
        this._handleNext = () => this._navigateEnhancedRail(1);
        this._handlePaginationClick = ({ target }) => {
            const button = target.closest('button[data-page]');
            if(button) {
                this._goToEnhancedPage(+button.dataset.page);
            }
        };
        this.addEventListener('scroll', this._handleEnhancedScroll, { passive: true });
        this.addEventListener('pointerdown', this._handleEnhancedInteraction, { passive: true });
        this.addEventListener('touchstart', this._handleEnhancedInteraction, { passive: true });
        this.addEventListener('wheel', this._handleEnhancedInteraction, { passive: true });
        this.previousButton?.addEventListener('click', this._handlePrevious);
        this.nextButton?.addEventListener('click', this._handleNext);
        this.pagePagination?.addEventListener('click', this._handlePaginationClick);
        this._handleEnhancedMediaChange = this._syncEnhancedWrapperPosition.bind(this);
        MOBILE_MEDIA_QUERY.addEventListener('change', this._handleEnhancedMediaChange);
        this._syncEnhancedWrapperPosition();

        this.enhancedResizeObserver = new ResizeObserver(() => {
            this._scheduleEnhancedRefresh();
        });
        this.enhancedResizeObserver.observe(this);
        if(wrapper) {
            this.enhancedResizeObserver.observe(wrapper);
        }
    }

    _disableEnhancedMode() {
        const state = this.enhancedControlState;

        this.removeEventListener('scroll', this._handleEnhancedScroll);
        this.removeEventListener('pointerdown', this._handleEnhancedInteraction);
        this.removeEventListener('touchstart', this._handleEnhancedInteraction);
        this.removeEventListener('wheel', this._handleEnhancedInteraction);
        this.previousButton?.removeEventListener('click', this._handlePrevious);
        this.nextButton?.removeEventListener('click', this._handleNext);
        this.pagePagination?.removeEventListener('click', this._handlePaginationClick);
        MOBILE_MEDIA_QUERY.removeEventListener('change', this._handleEnhancedMediaChange);
        this.enhancedResizeObserver?.disconnect();
        cancelAnimationFrame(this.enhancedRefreshFrame);
        cancelAnimationFrame(this.enhancedScrollAnimationFrame);
        this.enhancedScrollAnimationFrame = null;
        if(this.enhancedScrollSnapType !== null) {
            this.style.scrollSnapType = this.enhancedScrollSnapType;
            this.enhancedScrollSnapType = null;
        }
        clearTimeout(this.enhancedScrollTimer);
        this.enhancedTargetPage = null;
        this._restoreEnhancedWrapperPosition();

        state?.wrapper?.removeAttribute('data-enhanced-overflow');
        state?.wrapper?.removeAttribute('data-enhanced-can-previous');
        state?.wrapper?.removeAttribute('data-enhanced-can-next');
        this._enhancedItems.forEach((item) => {
            item.removeAttribute('data-enhanced-page-start');
            item.removeAttribute('data-enhanced-page-end');
        });
        this.previousButton?.setAttribute('hidden', '');
        this.nextButton?.setAttribute('hidden', '');
        this.pagePagination?.setAttribute('hidden', '');
        this.pagePagination?.replaceChildren();
        this.pageProgress?.setAttribute('hidden', '');
        state?.gallery?.querySelector('.\\#product-gallery-stage')
            ?.style.removeProperty(ENHANCED_CONTROLS_OFFSET_STYLE_PROP);
        state?.gallery?.querySelectorAll('product-gallery-slider .blaze-container')
            .forEach((container) => container.style.removeProperty(ENHANCED_MAIN_ARROW_TOP_STYLE_PROP));
        this.classList.remove(ENHANCED_POSITION_CLASS);
        if(state?.positionClass) {
            this.classList.add(state.positionClass);
        }
        this.toggleAttribute('auto-scroll', state?.autoScroll === true);

        if(state?.gallery) {
            state.gallery.classList.remove('@thumbs-position:bottom');
            if(state.galleryPositionClass) {
                state.gallery.classList.add(state.galleryPositionClass);
            }
        }
        this.scrollLeft = state?.scrollLeft || 0;
        this.height = null;
        this.parentHeight = null;
        this.halfParentHeight = null;
        this.enhancedControlState = null;
        this.enhancedPageState = null;
        this.previousButton = null;
        this.nextButton = null;
        this.pagePagination = null;
        this.pageProgress = null;

        const currentThumb = this.$('input').find((input) => input.checked);
        if(currentThumb) {
            this._scrollToTarget(currentThumb);
        }
    }

    _syncEnhancedWrapperPosition() {
        const state = this.enhancedControlState;
        if(!state?.pagination || !state.wrapper) {
            return;
        }

        if(MOBILE_MEDIA_QUERY.matches) {
            this._moveEnhancedWrapper(state.pagination.parentNode, state.pagination);
        } else {
            this._restoreEnhancedWrapperPosition();
        }
    }

    _restoreEnhancedWrapperPosition() {
        const state = this.enhancedControlState;
        if(!state?.wrapper || !state.wrapperParent) {
            return;
        }

        const nextSibling = state.wrapperNextSibling?.parentNode === state.wrapperParent
            ? state.wrapperNextSibling
            : null;
        this._moveEnhancedWrapper(state.wrapperParent, nextSibling);
    }

    _moveEnhancedWrapper(parent, before) {
        const wrapper = this.enhancedControlState?.wrapper;
        if(!wrapper || !parent || (wrapper.parentNode === parent && wrapper.nextSibling === before)) {
            return;
        }

        this.movingEnhancedWrapper = true;
        try {
            if(typeof parent.moveBefore === 'function') {
                parent.moveBefore(wrapper, before);
            } else {
                parent.insertBefore(wrapper, before);
            }
        } finally {
            this.movingEnhancedWrapper = false;
        }
    }

    _scheduleEnhancedRefresh() {
        if(!this.enhancedMode) {
            return;
        }
        cancelAnimationFrame(this.enhancedRefreshFrame);
        this.enhancedRefreshFrame = requestAnimationFrame(() => {
            this._refreshEnhancedPages();
        });
    }

    _refreshEnhancedPages() {
        if(!this.enhancedMode) {
            return;
        }

        const wrapper = this.enhancedControlState?.wrapper;
        const items = this._enhancedItems;
        const contentWidth = items.length
            ? items[items.length - 1].offsetLeft
                + items[items.length - 1].offsetWidth
                - items[0].offsetLeft
            : 0;
        const hasOverflow = contentWidth - (wrapper?.clientWidth || 0) > SCROLL_EPSILON;

        wrapper?.toggleAttribute('data-enhanced-overflow', hasOverflow);
        if(!hasOverflow) {
            this.enhancedTargetPage = null;
            items.forEach((item, index) => {
                item.toggleAttribute('data-enhanced-page-start', index === 0);
                item.removeAttribute('data-enhanced-page-end');
            });
            this.enhancedPageState = {
                visibleThumbnails: Math.max(items.length, 1),
                pageAdvance: Math.max(items.length, 1),
                pageCount: 1,
                currentPage: 0,
                targets: [0]
            };
            this.scrollLeft = 0;
            this._renderEnhancedPagination();
            this._updateEnhancedControlsOffset();
            this._updateEnhancedControls();
            return;
        }

        const itemWidth = items[0]?.offsetWidth || 1;
        const step = items[1]
            ? items[1].offsetLeft - items[0].offsetLeft
            : itemWidth;
        const gap = Math.max(0, step - itemWidth);
        const visibleThumbnails = Math.max(
            1,
            Math.floor((this.clientWidth + gap) / step)
        );
        const pageAdvance = Math.max(1, visibleThumbnails - 2);
        const targets = this._getEnhancedPageTargets(items, pageAdvance);
        const pageCount = targets.length;
        const activeIndex = Math.max(0, items.findIndex((item) => {
            return item.querySelector('[data-element="input"]')?.checked;
        }));
        const currentPage = this._getEnhancedPageForItem(
            activeIndex,
            visibleThumbnails,
            pageAdvance,
            pageCount
        );
        items.forEach((item, index) => {
            item.toggleAttribute(
                'data-enhanced-page-start',
                targets.some((target) => Math.abs(item.offsetLeft - target) <= SCROLL_EPSILON)
            );
            item.toggleAttribute('data-enhanced-page-end', index === items.length - 1);
        });

        this.enhancedPageState = {
            visibleThumbnails,
            pageAdvance,
            pageCount,
            currentPage,
            targets
        };
        this._renderEnhancedPagination();
        this._updateEnhancedControlsOffset();
        this._goToEnhancedPage(currentPage, { behavior: 'auto' });
    }

    _updateEnhancedControlsOffset() {
        const gallery = this.enhancedControlState?.gallery;
        const stage = gallery?.querySelector('.\\#product-gallery-stage');
        if(!stage) {
            return;
        }
        gallery.querySelectorAll('product-gallery-slider .blaze-container')
            .forEach((container) => container.style.removeProperty(ENHANCED_MAIN_ARROW_TOP_STYLE_PROP));
        if(!MOBILE_MEDIA_QUERY.matches) {
            stage.style.removeProperty(ENHANCED_CONTROLS_OFFSET_STYLE_PROP);
            return;
        }

        const track = Array.from(
            gallery.querySelectorAll('product-gallery-slider .blaze-track-container')
        ).find((element) => element.getClientRects().length);
        if(!track) {
            stage.style.removeProperty(ENHANCED_CONTROLS_OFFSET_STYLE_PROP);
            return;
        }

        const controlsOffset = Math.max(
            0,
            stage.getBoundingClientRect().bottom - track.getBoundingClientRect().bottom
        );
        const blazeContainer = track.closest('.blaze-container');
        if(blazeContainer) {
            const trackRect = track.getBoundingClientRect();
            const containerRect = blazeContainer.getBoundingClientRect();
            blazeContainer.style.setProperty(
                ENHANCED_MAIN_ARROW_TOP_STYLE_PROP,
                `${trackRect.top - containerRect.top + trackRect.height / 2}px`
            );
        }
        stage.style.setProperty(
            ENHANCED_CONTROLS_OFFSET_STYLE_PROP,
            `${controlsOffset}px`
        );
    }

    _navigateEnhancedRail(direction) {
        const state = this.enhancedPageState;
        if(!state) {
            return;
        }
        this._goToEnhancedPage(state.currentPage + direction);
    }

    _ensureEnhancedThumbnailVisible(label) {
        const item = label?.parentElement;
        const state = this.enhancedPageState;
        if(!item || !state) {
            this._scheduleEnhancedRefresh();
            return;
        }

        const items = this._enhancedItems;
        const itemIndex = items.indexOf(item);
        if(itemIndex < 0) {
            return;
        }
        const targetPage = this._getEnhancedPageForItem(
            itemIndex,
            state.visibleThumbnails,
            state.pageAdvance,
            state.pageCount
        );
        state.targets = this._getEnhancedPageTargets(
            items,
            state.pageAdvance
        );
        if(targetPage !== state.currentPage) {
            this._goToEnhancedPage(targetPage);
        } else {
            this._updateEnhancedControls();
        }
    }

    _goToEnhancedPage(page, { behavior = 'smooth' } = {}) {
        const state = this.enhancedPageState;
        if(!state) {
            return;
        }
        const targetPage = Math.max(0, Math.min(state.pageCount - 1, page));
        state.currentPage = targetPage;
        this.enhancedTargetPage = targetPage;
        this._scrollToEnhancedTarget(state.targets[targetPage] || 0, behavior);
        this._renderEnhancedPagination();
        this._updateEnhancedControls();
    }

    _scrollToEnhancedTarget(target, behavior = 'smooth') {
        cancelAnimationFrame(this.enhancedScrollAnimationFrame);
        this.enhancedScrollAnimationFrame = null;

        if(
            behavior !== 'smooth'
            || window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ) {
            this.scrollLeft = target;
            return;
        }

        const start = this.scrollLeft;
        const distance = target - start;
        if(Math.abs(distance) <= SCROLL_EPSILON) {
            this.scrollLeft = target;
            return;
        }

        const startedAt = performance.now();
        this.enhancedScrollSnapType = this.style.scrollSnapType;
        this.style.scrollSnapType = 'none';
        const animate = (now) => {
            const progress = Math.min(1, (now - startedAt) / ENHANCED_SCROLL_DURATION);
            const eased = progress < 0.5
                ? 4 * progress * progress * progress
                : 1 - Math.pow(-2 * progress + 2, 3) / 2;
            this.scrollLeft = start + distance * eased;
            if(progress < 1) {
                this.enhancedScrollAnimationFrame = requestAnimationFrame(animate);
            } else {
                this.enhancedScrollAnimationFrame = null;
                this.scrollLeft = target;
                this.style.scrollSnapType = this.enhancedScrollSnapType;
                this.enhancedScrollSnapType = null;
            }
        };
        this.enhancedScrollAnimationFrame = requestAnimationFrame(animate);
    }

    _syncEnhancedPageFromScroll() {
        const state = this.enhancedPageState;
        if(!state?.targets.length) {
            return;
        }
        if(Number.isInteger(this.enhancedTargetPage)) {
            state.currentPage = this.enhancedTargetPage;
            this._renderEnhancedPagination();
            this._updateEnhancedControls();
            return;
        }
        state.currentPage = state.targets.reduce((nearestPage, target, page) => {
            return Math.abs(target - this.scrollLeft)
                < Math.abs(state.targets[nearestPage] - this.scrollLeft)
                ? page
                : nearestPage;
        }, 0);
        this._renderEnhancedPagination();
        this._updateEnhancedControls();
    }

    _snapToNearestEnhancedPage() {
        const state = this.enhancedPageState;
        if(!state) {
            return;
        }
        if(this.enhancedScrollAnimationFrame) {
            return;
        }
        if(Number.isInteger(this.enhancedTargetPage)) {
            const targetPage = this.enhancedTargetPage;
            const target = state.targets[targetPage] || 0;
            this.enhancedTargetPage = null;
            state.currentPage = targetPage;
            if(Math.abs(this.scrollLeft - target) > SCROLL_EPSILON) {
                this.scrollTo({ left: target, behavior: 'auto' });
            }
            this._renderEnhancedPagination();
            this._updateEnhancedControls();
            return;
        }
        const target = state.targets[state.currentPage] || 0;
        if(Math.abs(this.scrollLeft - target) > SCROLL_EPSILON) {
            this._goToEnhancedPage(state.currentPage);
        }
    }

    _renderEnhancedPagination() {
        const pagination = this.pagePagination;
        const progress = this.pageProgress;
        const state = this.enhancedPageState;
        if(!state) {
            return;
        }

        const useProgress = this.enhancedControlState?.wrapper
            ?.dataset.enhancedNavigationMode === 'progress';
        const hasPages = state.pageCount > 1;
        pagination?.toggleAttribute('hidden', useProgress || !hasPages);
        progress?.toggleAttribute('hidden', !useProgress || !hasPages);

        if(useProgress && hasPages) {
            const totalItems = this._enhancedItems.length;
            const maxPosition = Math.max(0, totalItems - state.visibleThumbnails);
            const currentIndex = state.currentPage === state.pageCount - 1
                ? maxPosition
                : state.currentPage * state.pageAdvance;
            Global.CarouselProgress?.update({
                track: progress,
                totalItems,
                visibleItems: state.visibleThumbnails,
                currentIndex
            });
        }

        if(!pagination) {
            return;
        }
        if(!useProgress && pagination.children.length !== state.pageCount) {
            pagination.replaceChildren(...Array.from({ length: state.pageCount }, (_, page) => {
                const button = document.createElement('button');
                button.type = 'button';
                button.dataset.page = page;
                button.textContent = `${page + 1}`;
                button.ariaLabel = `${page + 1} of ${state.pageCount}`;
                return button;
            }));
        } else if(useProgress && pagination.children.length) {
            pagination.replaceChildren();
        }

        Array.from(pagination.children).forEach((button, page) => {
            button.classList.toggle('active', page === state.currentPage);
            if(page === state.currentPage) {
                button.setAttribute('aria-current', 'true');
            } else {
                button.removeAttribute('aria-current');
            }
        });
    }

    _updateEnhancedControls() {
        const state = this.enhancedPageState;
        const wrapper = this.enhancedControlState?.wrapper;
        if(!state || state.pageCount <= 1) {
            this.previousButton?.setAttribute('hidden', '');
            this.nextButton?.setAttribute('hidden', '');
            wrapper?.removeAttribute('data-enhanced-can-previous');
            wrapper?.removeAttribute('data-enhanced-can-next');
            return;
        }
        const canPrevious = state.currentPage > 0;
        const canNext = state.currentPage < state.pageCount - 1;
        this.previousButton?.toggleAttribute('hidden', !canPrevious);
        this.nextButton?.toggleAttribute(
            'hidden',
            !canNext
        );
        wrapper?.toggleAttribute('data-enhanced-can-previous', canPrevious);
        wrapper?.toggleAttribute('data-enhanced-can-next', canNext);
    }

    _getEnhancedPageTargets(items, pageAdvance) {
        const maxScroll = Math.max(0, this.scrollWidth - this.clientWidth);
        const targets = [0];

        for(let itemIndex = pageAdvance; itemIndex < items.length; itemIndex += pageAdvance) {
            const target = items[itemIndex]?.offsetLeft || 0;
            if(target >= maxScroll - SCROLL_EPSILON) {
                break;
            }
            targets.push(target);
        }
        if(maxScroll > targets[targets.length - 1] + SCROLL_EPSILON) {
            targets.push(maxScroll);
        }
        return targets;
    }

    _getEnhancedPageForItem(itemIndex, visibleThumbnails, pageAdvance, pageCount) {
        const firstPageVisible = Math.max(1, visibleThumbnails - 1);
        if(itemIndex < firstPageVisible) {
            return 0;
        }
        return Math.min(
            pageCount - 1,
            1 + Math.floor((itemIndex - firstPageVisible) / pageAdvance)
        );
    }

    get _enhancedItems() {
        return Array.from(this.children).filter((item) => {
            return item.classList.contains('#product-gallery-thumbnails-thumbnail');
        });
    }

    destroy() {
        if(this.enhancedMode) {
            this.enhancedMode = false;
            this._disableEnhancedMode();
            this.removeAttribute('data-gallery-enhanced');
        }
    }
});
